import { ActionProps } from '@/types/actionProps';
import { Locale } from '@/types/locale';
import { ParsedUrlQueryInput } from 'querystring';
import { UrlObject } from 'url';
import { COUNTRY_PUBLIC_LINK } from '@/constants/country';

const CSOB_PROMO_SOURCE_PREFIX = 'CSOB';
const REGISTRATION_PROMO_LOCALE: Locale = 'cs';
type LinkTarget = NonNullable<ActionProps['to']>;

interface GetProductRegistrationLinkOptions<T extends LinkTarget> {
	currentPromoSource?: string | null;
	defaultPromoSource: string;
	locale?: Locale;
	registrationLink: T;
	registrationPath: string;
}

interface WithPromoSourceForRegistrationLinkOptions<T extends LinkTarget> {
	link: T;
	locale?: Locale;
	promoSource?: string | null;
	registrationPath: string;
}

function getBaseUrl(locale?: Locale): string {
	if (locale && locale in COUNTRY_PUBLIC_LINK) {
		return COUNTRY_PUBLIC_LINK[locale as keyof typeof COUNTRY_PUBLIC_LINK];
	}

	return COUNTRY_PUBLIC_LINK.cs;
}

function getLinkPathname(link?: ActionProps['to']): string | undefined {
	if (!link) {
		return undefined;
	}

	if (typeof link === 'string') {
		return new URL(link, getBaseUrl()).pathname;
	}

	return link.pathname?.toString();
}

function getQueryObject(query: UrlObject['query']): ParsedUrlQueryInput {
	if (!query || typeof query === 'string') {
		return {};
	}

	return query;
}

export function isCsobPromoSource(promoSource?: string | null): boolean {
	return promoSource?.toUpperCase().startsWith(CSOB_PROMO_SOURCE_PREFIX) ?? false;
}

export function isRegistrationLink(link: ActionProps['to'], registrationPath: string): boolean {
	return getLinkPathname(link) === registrationPath;
}

export function withPromoSourceForRegistrationLink<T extends LinkTarget>({
	link,
	locale = 'cs',
	promoSource,
	registrationPath,
}: WithPromoSourceForRegistrationLinkOptions<T>): T {
	if (
		locale !== REGISTRATION_PROMO_LOCALE ||
		!promoSource ||
		!isRegistrationLink(link, registrationPath)
	) {
		return link;
	}

	if (typeof link === 'string') {
		const url = new URL(link, getBaseUrl(locale));
		url.searchParams.set('promo', promoSource);

		return `${url.pathname}${url.search}${url.hash}` as T;
	}

	const urlObject = link as UrlObject;

	return {
		...urlObject,
		query: {
			...getQueryObject(urlObject.query),
			promo: promoSource,
		},
	} as unknown as T;
}

export function getProductRegistrationLink<T extends LinkTarget>({
	currentPromoSource,
	defaultPromoSource,
	locale = 'cs',
	registrationLink,
	registrationPath,
}: GetProductRegistrationLinkOptions<T>): T {
	const promoSource = isCsobPromoSource(currentPromoSource)
		? currentPromoSource
		: defaultPromoSource;

	return withPromoSourceForRegistrationLink({
		link: registrationLink,
		locale,
		promoSource,
		registrationPath,
	});
}
